import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Format date to "YYYY-MM-DD HH:MM" format
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  const d = new Date(date);
  
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  return dateFormatter.format(d).replace(',', '');
};

/**
 * Format number as Japanese Yen currency
 * @param {number} amount - Number to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, prefix='¥') => {
  return `${prefix}${amount.toLocaleString()}`;
};

/**
 * Format phone number to "xxx-xxxx-xxxx" format
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} Formatted phone number string
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '-';
  
  // Remove non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Format to xxx-xxxx-xxxx
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
  }
  
  // Return original if not the expected length
  return phoneNumber;
};

/**
 * Format relative time with i18n translations
 * @param {string} dateString - ISO date string to format
 * @param {object} t - i18next translation function
 * @returns {string} Formatted relative time string
 */
export const formatRelativeTime = (dateString, t) => {
  if (!t) {
    console.warn('Translation function not provided to formatRelativeTime');
    // Fallback to English if translation function isn't provided
    return formatRelativeTimeEnglish(dateString);
  }

  const date = new Date(dateString);
  const now = new Date();
  
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  // Less than 1 minute: show "1分前"
  if (diffMinutes < 1) {
    return t('common.time.minute_ago', { count: 1 });
  }
  
  // Less than 1 hour: show "X分前"
  if (diffMinutes < 60) {
    return t('common.time.minutes_ago', { count: diffMinutes });
  }
  
  // Less than 24 hours: show "X時間前"
  if (diffHours < 24) {
    return t('common.time.hours_ago', { count: diffHours });
  }
  
  // Less than 3 days: show "X日前"
  if (diffDays < 3) {
    return t('common.time.days_ago', { count: diffDays });
  }
  
  // Greater than or equal to 3 days: show "yyyy/MM/dd"
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}/${month}/${day}`;
};

// Fallback function for when translation isn't available
const formatRelativeTimeEnglish = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  // Just now
  if (diffMinutes === 0) {
    return 'Just now';
  }
  
  // Within 1 hour: "N minutes ago"
  if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  
  // Within 24 hours: "N hours ago"
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  }
  
  // Within 30 days: "N days ago"
  if (diffDays < 30) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  }
  
  // Beyond 30 days
  const year = date.getFullYear();
  const month = date.toLocaleString('default', { month: 'short' });
  const day = date.getDate();
  
  // Different year: "Year Month Day"
  if (year !== now.getFullYear()) {
    return `${year} ${month} ${day}`;
  }
  
  // Same year: "Month Day"
  return `${month} ${day}`;
};

/**
 * Format date to "YYYY/MM/DD" format for tooltips
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForTooltip = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}/${month}/${day}`;
};

/**
 * Format date to "YYYY-MM-DD" format
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateOnly = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return dateFormatter.format(d);
};

/**
 * Format date to ISO string (e.g., "2025-06-12T16:00:00.000Z")
 * @param {string|Date} date - Date to format
 * @param {boolean} isEndDate - Whether this is an end date (sets time to end of day)
 * @returns {string} Formatted date string
 */
export const formatDateWithTimezone = (date, isEndDate = false) => {
  if (!date) return '';

  let dayjsDate = dayjs(date);

  if (isEndDate) {
    // For end dates, set time to end of day
    dayjsDate = dayjsDate.endOf('day');
  } else {
    // For start dates, set time to beginning of day
    dayjsDate = dayjsDate.startOf('day');
  }

  return dayjsDate.utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
};

/**
 * Format date to "YYYY年MM月DD日HH:MM" format for Japanese countdown deadline
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatCountdownDeadline = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');

  return `${year}年${month}月${day}日${hours}:${minutes}`;
};

/**
 * Format notification time based on date rules:
 * - This year: show date and time like "11/28 12:26"
 * - Not this year: show date and time like "2024/11/26 12:26"
 * @param {string|Date} createdAt - Date to format
 * @returns {string} Formatted time string
 */
export const formatNotificationTime = (createdAt) => {
  try {
    const date = new Date(createdAt);
    const now = new Date();

    // Get time components
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}`;

    // Check if it's this year
    const isThisYear = date.getFullYear() === now.getFullYear();

    if (isThisYear) {
      // Show date without year (MM/DD HH:MM format)
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${month}/${day} ${timeStr}`;
    }

    // Show date with year (YYYY/MM/DD HH:MM format)
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}/${month}/${day} ${timeStr}`;
  } catch (error) {
    return '';
  }
};